import { dayjsUtils } from '@shared/utils/helpers/dayjs';
import type { TAppointment } from '@shared/types/appointment';
import {
  APPOINTMENTS_INTERVIEW_TYPES,
  APPOINTMENTS_PLATFORMS,
  APPOINTMENTS_TYPES,
} from '@shared/types/appointment';
import { mixed, object, string } from 'yup';

export const appointmentFormNames = {
  interviewType: 'interviewType',
  host: 'host',
  appointmentType: 'appointmentType',
  name: 'name',
  date: 'date',
  time: 'time',
  timezone: 'timezone',
  description: 'description',
  meetingPlatform: 'meetingPlatform',
  meetingLink: 'meetingLink',
  location: 'location',
  country: 'country',
  city: 'city',
} as const;

export const appointmentFormInitialValues = {
  interviewType: '' as TAppointment['interview_type'],

  host: '' as unknown as TAppointment['host'],

  appointmentType: APPOINTMENTS_TYPES.online as TAppointment['type'],
  date: '',
  time: '',
  timezone: '',
  name: '',
  description: '',

  // ONLINE
  meetingPlatform: APPOINTMENTS_PLATFORMS.google as TAppointment['platform'],
  meetingLink: '',

  // ONSITE
  location: '',
  country: '',
  city: '',
};

const {
  appointmentType,
  city,
  country,
  date,
  description,
  meetingPlatform,
  name,
  time,
  timezone,
} = appointmentFormNames;

export const createInitialValueFromExistingAppointment = (
  appointmentData?: TAppointment
) => {
  if (!appointmentData) return undefined;
  const date = dayjsUtils(appointmentData.start_time)
    .tz(appointmentData.timezone)
    .format('YYYY-MM-DDTHH:mm:ssZ');

  return {
    interviewType: appointmentData.interview_type,
    host: appointmentData.host,
    appointmentType: appointmentData.type,
    date: date.split('T')[0],
    time: date.split('T')[1],
    timezone: appointmentData.timezone,
    name: appointmentData.name,
    description: appointmentData.description,
    meetingPlatform: appointmentData.platform ?? '',
    meetingLink: appointmentData.link ?? '',
    location: appointmentData.location ?? '',
    country: appointmentData.country.code ?? '',
    city: appointmentData.city ?? '',
  };
};

const generateRequiredFieldMessage = (label: string) => {
  return `Please enter ${label}`;
};
const VALID_URL_MESSAGE = 'Please enter a valid URL';

export const appointmentFormSchema = object().shape({
  // Basic information
  interviewType: string()
    .oneOf(
      Object.values(APPOINTMENTS_INTERVIEW_TYPES),
      `Interview type must be either ${Object.values(APPOINTMENTS_INTERVIEW_TYPES).join(', ')}`
    )
    .required(generateRequiredFieldMessage('Interview Type')),
  host: mixed().nullable(),
  appointmentType: string()
    .oneOf(
      Object.values(APPOINTMENTS_TYPES),
      `type must be either ${Object.values(APPOINTMENTS_TYPES).join(', ')}`
    )
    .required(generateRequiredFieldMessage(appointmentType)),

  // Timing information
  date: string().required(generateRequiredFieldMessage(date)),
  time: string().required(generateRequiredFieldMessage(time)),
  timezone: string().required(generateRequiredFieldMessage(timezone)),

  name: string().required(generateRequiredFieldMessage(name)),

  // Description
  description: string()
    .min(10, 'Description should be at least 10 characters')
    .required(generateRequiredFieldMessage(description)),

  // Online meeting details (conditionally required based on type)
  meetingPlatform: string()
    .oneOf(
      Object.values(APPOINTMENTS_PLATFORMS),
      `type must be either ${Object.values(APPOINTMENTS_PLATFORMS).join(', ')}`
    )
    .when(appointmentType, {
      is: APPOINTMENTS_TYPES.online,
      then: (schema) =>
        schema.required(generateRequiredFieldMessage(meetingPlatform)),
      otherwise: (schema) => schema,
    }),
  meetingLink: string().when(appointmentType, {
    is: APPOINTMENTS_TYPES.online,
    then: (schema) => schema.url(VALID_URL_MESSAGE),
    otherwise: (schema) => schema,
  }),

  // Onsite location details (conditionally required based on type)
  location: string().when(appointmentType, {
    is: APPOINTMENTS_TYPES.onsite,
    then: (schema) => schema,
    otherwise: (schema) => schema,
  }),
  country: string().when(appointmentType, {
    is: APPOINTMENTS_TYPES.onsite,
    then: (schema) => schema.required(generateRequiredFieldMessage(country)),
    otherwise: (schema) => schema,
  }),
  city: string().when(appointmentType, {
    is: APPOINTMENTS_TYPES.onsite,
    then: (schema) => schema.required(generateRequiredFieldMessage(city)),
    otherwise: (schema) => schema,
  }),
});

export type TAppointmentFormValues = typeof appointmentFormInitialValues;
